import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Row, Col, Button, Alert, Badge } from 'react-bootstrap';
import {

  BsHeadset,
  BsPlusCircle,
  BsRobot,
  BsSave,
  BsTrash,
  BsWhatsapp,
} from 'react-icons/bs';
import UserLayout from '../../components/user/UserLayout';
import api from '../../lib/api';
import { useToast } from '../../components/common/ToastProvider';

const waDefault = {
  verify_token: '',
  phone_number_id: '',
  api_token: '',
  webhook_enabled: false,
  menu_title: 'Welcome',
  menu_body: 'Hello, how can we help?',
  menu_button_text: 'Menus',
  menu_options: [],
  quick_reply_intro_text: 'How can I help you next?',
  quick_reply_intro_options: [],
  quick_reply_stuck_text:
    "It looks like you're still facing issues. Would you like to talk to a real person?",
  quick_reply_stuck_options: [],
  action_reply_map: {},
  support_email: '',
  support_phone: '',
};

const emptyMenuOption = { id: '', title: '', description: '' };
const emptyQuickReply = { id: '', title: '' };
const emptyActionReply = { id: '', text: '' };

const modelLabels = {
  tinyllama: 'TinyLlama',
  mistral: 'Mistral',
  openai: 'OpenAI',
  gemini: 'Gemini',
  groq: 'Groq',
};

function SectionCard({ title, icon, badge, children, className = '' }) {
  const Icon = icon;
  return (
    <Card className={`user-card shadow-sm border-0 rounded-4 h-100 ${className}`}>
      <Card.Body className="p-4 d-flex flex-column">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-3 d-inline-flex align-items-center justify-content-center"
              style={{
                width: 42,
                height: 42,
                background: 'rgba(13, 110, 253, 0.08)',
                color: '#0d6efd',
                flexShrink: 0,
              }}
            >
              <Icon size={18} />
            </div>
            <div className="user-section-title mb-0">{title}</div>
          </div>
          {badge || null}
        </div>
        <div className="flex-grow-1">{children}</div>
      </Card.Body>
    </Card>
  );
}

function SubSectionCard({ title, children }) {
  return (
    <Card
      className="border-0 rounded-4 h-100"
      style={{
        background: '#f8fafc',
        minHeight: '100%',
      }}
    >
      <Card.Body className="p-3">
        <div className="fw-semibold mb-3" style={{ color: '#0f172a' }}>
          {title}
        </div>
        {children}
      </Card.Body>
    </Card>
  );
}

function ActionButton({ children, ...props }) {
  return (
    <Button
      {...props}
      className={`user-quick-action d-inline-flex align-items-center justify-content-center gap-2 ${props.className || ''}`}
    >
      {children}
    </Button>
  );
}

export default function UserChannelsPage() {
  const [wa, setWa] = useState(waDefault);
  const [waActionRows, setWaActionRows] = useState([]);
  const [model, setModel] = useState({
    current_model: 'tinyllama',
    openai_key: '',
    gemini_key: '',
    groq_key: '',
    openai_model: '',
    gemini_model: '',
    groq_model: '',
  });
  const [agents, setAgents] = useState([]);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('success');
  const { showToast } = useToast();

  const getError = (e, fallback) =>
    e?.response?.data?.error || e?.response?.data?.detail || e?.message || fallback;

  useEffect(() => {
    const load = async () => {
      try {
        const [waRes, modelRes, agentRes] = await Promise.all([
          api.get('/user/settings/whatsapp/'),
          api.get('/chat-model/'),
          api.get('/user/settings/support-agents/'),
        ]);

        const waData = { ...waDefault, ...(waRes.data || {}) };
        setWa(waData);

        const actionMap = waData.action_reply_map || {};
        setWaActionRows(
          Object.entries(actionMap).map(([id, text]) => ({
            id,
            text: String(text || ''),
          }))
        );

        setModel((prev) => ({
          ...prev,
          ...(modelRes.data || {}),
        }));

        setAgents((agentRes.data || []).map((a) => ({ ...a, password: '' })));
      } catch (error) {
        setMsg(getError(error, 'Unable to load channel settings.'));
        setMsgType('danger');
      }
    };

    load();
  }, []);

  const activeProviderLabel = useMemo(
    () => modelLabels[model.current_model] || 'Provider',
    [model.current_model]
  );

  const updateOptionRow = (field, idx, patch) => {
    const rows = [...(wa[field] || [])];
    rows[idx] = { ...(rows[idx] || {}), ...patch };
    setWa({ ...wa, [field]: rows });
  };

  const addOptionRow = (field, template = {}) => {
    setWa({ ...wa, [field]: [...(wa[field] || []), template] });
  };

  const removeOptionRow = (field, idx) => {
    setWa({ ...wa, [field]: (wa[field] || []).filter((_, i) => i !== idx) });
  };

  const saveWA = async () => {
    try {
      const nextActionMap = {};
      waActionRows.forEach((row) => {
        const key = (row.id || '').trim();
        if (key) nextActionMap[key] = row.text || '';
      });

      await api.post('/user/settings/whatsapp/', {
        ...wa,
        action_reply_map: nextActionMap,
      });

      setMsg('WhatsApp settings saved');
      setMsgType('success');
      showToast('WhatsApp settings saved', 'success');
    } catch (error) {
      const errorMessage = getError(error, 'Failed to save WhatsApp settings');
      setMsg(errorMessage);
      setMsgType('danger');
      showToast(errorMessage, 'danger');
    }
  };

  const saveModel = async () => {
    try {
      const payload = {
        model: model.current_model,
      };

      if (model.current_model === 'openai') {
        payload.openai_key = model.openai_key;
        payload.openai_model = model.openai_model;
      }

      if (model.current_model === 'gemini') {
        payload.gemini_key = model.gemini_key;
        payload.gemini_model = model.gemini_model;
      }

      if (model.current_model === 'groq') {
        payload.groq_key = model.groq_key;
        payload.groq_model = model.groq_model;
      }

      await api.post('/chat-model/', payload);

      setMsg('Model settings saved');
      setMsgType('success');
      showToast('Model settings saved', 'success');
    } catch (error) {
      const errorMessage = getError(error, 'Failed to save model settings');
      setMsg(errorMessage);
      setMsgType('danger');
      showToast(errorMessage, 'danger');
    }
  };

  const saveAgents = async () => {
    try {
      await api.post('/user/settings/support-agents/', { agents });
      setMsg('Support agents saved');
      setMsgType('success');
      showToast('Support agents saved', 'success');
    } catch (error) {
      const errorMessage = getError(error, 'Failed to save support agents');
      setMsg(errorMessage);
      setMsgType('danger');
      showToast(errorMessage, 'danger');
    }
  };

  const addAgent = () => {
    setAgents([
      ...agents,
      {
        name: '',
        email: '',
        password: '',
        is_online: true,
        is_active: true,
        max_active_chats: 10,
      },
    ]);
  };

  const removeAgent = async (idx) => {
    try {
      const current = agents[idx];
      if (current?.id) {
        await api.delete(`/user/settings/support-agents/${current.id}/`);
      }
      setAgents(agents.filter((_, i) => i !== idx));
      showToast('Agent removed', 'success');
    } catch (error) {
      const errorMessage = getError(error, 'Failed to remove agent');
      setMsg(errorMessage);
      setMsgType('danger');
      showToast(errorMessage, 'danger');
    }
  };

  return (
    <UserLayout
      title="Channels & AI Model"
      subtitle="Configure WhatsApp automation, live support agents, and AI provider settings"
    >
      {msg ? <Alert variant={msgType}>{msg}</Alert> : null}

      <SectionCard
        title="WhatsApp Configuration"
        icon={BsWhatsapp}
        badge={
          <Badge bg={wa.webhook_enabled ? 'success' : 'secondary'} pill>
            {wa.webhook_enabled ? 'Enabled' : 'Disabled'}
          </Badge>
        }
      >
        <Row className="g-4">
          <Col xl={6}>
            <SubSectionCard title="Connection Settings">
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Verify Token</Form.Label>
                    <Form.Control
                      value={wa.verify_token || ''}
                      onChange={(e) => setWa({ ...wa, verify_token: e.target.value })}
                      placeholder="Verify token"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Phone Number ID</Form.Label>
                    <Form.Control
                      value={wa.phone_number_id || ''}
                      onChange={(e) => setWa({ ...wa, phone_number_id: e.target.value })}
                      placeholder="Phone number ID"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>API Token</Form.Label>
                    <Form.Control
                      value={wa.api_token || ''}
                      onChange={(e) => setWa({ ...wa, api_token: e.target.value })}
                      placeholder="API token"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Support Email</Form.Label>
                    <Form.Control
                      value={wa.support_email || ''}
                      onChange={(e) => setWa({ ...wa, support_email: e.target.value })}
                      placeholder="Support email"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Support Phone</Form.Label>
                    <Form.Control
                      value={wa.support_phone || ''}
                      onChange={(e) => setWa({ ...wa, support_phone: e.target.value })}
                      placeholder="Support phone"
                    />
                  </Form.Group>
                </Col>

                <Col md={12} className="pt-1">
                  <Form.Check
                    type="switch"
                    label="Enable WhatsApp Webhook"
                    checked={!!wa.webhook_enabled}
                    onChange={(e) => setWa({ ...wa, webhook_enabled: e.target.checked })}
                  />
                </Col>
              </Row>
            </SubSectionCard>
          </Col>

          <Col xl={6}>
            <SubSectionCard title="Menu Content">
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Menu Title</Form.Label>
                    <Form.Control
                      value={wa.menu_title || ''}
                      onChange={(e) => setWa({ ...wa, menu_title: e.target.value })}
                      placeholder="Welcome"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Menu Body</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={wa.menu_body || ''}
                      onChange={(e) => setWa({ ...wa, menu_body: e.target.value })}
                      placeholder="Hello, how can we help?"
                    />
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Menu Button Text</Form.Label>
                    <Form.Control
                      value={wa.menu_button_text || ''}
                      onChange={(e) => setWa({ ...wa, menu_button_text: e.target.value })}
                      placeholder="Menus"
                    />
                  </Form.Group>
                </Col>
              </Row>
            </SubSectionCard>
          </Col>

          <Col xl={12}>
            <SubSectionCard title="Menu Options">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="text-muted small">
                  Add action buttons users can select from the WhatsApp menu.
                </div>
                <ActionButton
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => addOptionRow('menu_options', emptyMenuOption)}
                >
                  <BsPlusCircle />
                  Add Option
                </ActionButton>
              </div>

              {(wa.menu_options || []).length === 0 ? (
                <div className="text-muted small">No menu options added yet.</div>
              ) : (
                (wa.menu_options || []).map((row, idx) => (
                  <Row className="g-3 mb-3" key={`menu-${idx}`}>
                    <Col lg={3}>
                      <Form.Group>
                        <Form.Label>Option ID</Form.Label>
                        <Form.Control
                          value={row.id || ''}
                          placeholder="e.g. start_chat"
                          onChange={(e) =>
                            updateOptionRow('menu_options', idx, { id: e.target.value })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col lg={4}>
                      <Form.Group>
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          value={row.title || ''}
                          placeholder="Button title"
                          onChange={(e) =>
                            updateOptionRow('menu_options', idx, { title: e.target.value })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col lg={4}>
                      <Form.Group>
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          value={row.description || ''}
                          placeholder="Description"
                          onChange={(e) =>
                            updateOptionRow('menu_options', idx, {
                              description: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col lg={1} className="d-flex align-items-end">
                      <ActionButton
                        variant="outline-danger"
                        className="w-100"
                        onClick={() => removeOptionRow('menu_options', idx)}
                      >
                        <BsTrash />
                      </ActionButton>
                    </Col>
                  </Row>
                ))
              )}
            </SubSectionCard>
          </Col>

          <Col xl={6}>
            <SubSectionCard title="Intro Quick Replies">
              <Row className="g-3 mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Intro Prompt</Form.Label>
                    <Form.Control
                      value={wa.quick_reply_intro_text || ''}
                      onChange={(e) =>
                        setWa({ ...wa, quick_reply_intro_text: e.target.value })
                      }
                      placeholder="How can I help you next?"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="text-muted small">Suggested quick replies for the first stage.</div>
                <ActionButton
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => addOptionRow('quick_reply_intro_options', emptyQuickReply)}
                >
                  <BsPlusCircle />
                  Add Reply
                </ActionButton>
              </div>

              {(wa.quick_reply_intro_options || []).length === 0 ? (
                <div className="text-muted small">No intro quick replies added yet.</div>
              ) : (
                (wa.quick_reply_intro_options || []).map((row, idx) => (
                  <Row className="g-3 mb-3" key={`intro-${idx}`}>
                    <Col md={5}>
                      <Form.Group>
                        <Form.Label>ID</Form.Label>
                        <Form.Control
                          value={row.id || ''}
                          placeholder="id"
                          onChange={(e) =>
                            updateOptionRow('quick_reply_intro_options', idx, {
                              id: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          value={row.title || ''}
                          placeholder="title"
                          onChange={(e) =>
                            updateOptionRow('quick_reply_intro_options', idx, {
                              title: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col md={1} className="d-flex align-items-end">
                      <ActionButton
                        variant="outline-danger"
                        className="w-100"
                        onClick={() => removeOptionRow('quick_reply_intro_options', idx)}
                      >
                        <BsTrash />
                      </ActionButton>
                    </Col>
                  </Row>
                ))
              )}
            </SubSectionCard>
          </Col>

          <Col xl={6}>
            <SubSectionCard title="Stuck-State Quick Replies">
              <Row className="g-3 mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Stuck Prompt</Form.Label>
                    <Form.Control
                      value={wa.quick_reply_stuck_text || ''}
                      onChange={(e) =>
                        setWa({ ...wa, quick_reply_stuck_text: e.target.value })
                      }
                      placeholder="It looks like you're still facing issues..."
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="text-muted small">
                  Replies shown when the user needs more help.
                </div>
                <ActionButton
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => addOptionRow('quick_reply_stuck_options', emptyQuickReply)}
                >
                  <BsPlusCircle />
                  Add Reply
                </ActionButton>
              </div>

              {(wa.quick_reply_stuck_options || []).length === 0 ? (
                <div className="text-muted small">No stuck-state quick replies added yet.</div>
              ) : (
                (wa.quick_reply_stuck_options || []).map((row, idx) => (
                  <Row className="g-3 mb-3" key={`stuck-${idx}`}>
                    <Col md={5}>
                      <Form.Group>
                        <Form.Label>ID</Form.Label>
                        <Form.Control
                          value={row.id || ''}
                          placeholder="id"
                          onChange={(e) =>
                            updateOptionRow('quick_reply_stuck_options', idx, {
                              id: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Title</Form.Label>
                        <Form.Control
                          value={row.title || ''}
                          placeholder="title"
                          onChange={(e) =>
                            updateOptionRow('quick_reply_stuck_options', idx, {
                              title: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col md={1} className="d-flex align-items-end">
                      <ActionButton
                        variant="outline-danger"
                        className="w-100"
                        onClick={() => removeOptionRow('quick_reply_stuck_options', idx)}
                      >
                        <BsTrash />
                      </ActionButton>
                    </Col>
                  </Row>
                ))
              )}
            </SubSectionCard>
          </Col>

          <Col xl={12}>
            <SubSectionCard title="Action Response Messages">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div className="text-muted small">
                  Map action IDs to automatic WhatsApp response messages.
                </div>
                <ActionButton
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => setWaActionRows([...(waActionRows || []), emptyActionReply])}
                >
                  <BsPlusCircle />
                  Add Mapping
                </ActionButton>
              </div>

              {waActionRows.length === 0 ? (
                <div className="text-muted small">No action mappings added yet.</div>
              ) : (
                waActionRows.map((row, idx) => (
                  <Row className="g-3 mb-3" key={`map-${idx}`}>
                    <Col lg={3}>
                      <Form.Group>
                        <Form.Label>Action ID</Form.Label>
                        <Form.Control
                          value={row.id || ''}
                          placeholder="action id"
                          onChange={(e) => {
                            const next = [...waActionRows];
                            next[idx] = { ...next[idx], id: e.target.value };
                            setWaActionRows(next);
                          }}
                        />
                      </Form.Group>
                    </Col>

                    <Col lg={8}>
                      <Form.Group>
                        <Form.Label>Auto Response Text</Form.Label>
                        <Form.Control
                          value={row.text || ''}
                          placeholder="Auto-response text"
                          onChange={(e) => {
                            const next = [...waActionRows];
                            next[idx] = { ...next[idx], text: e.target.value };
                            setWaActionRows(next);
                          }}
                        />
                      </Form.Group>
                    </Col>

                    <Col lg={1} className="d-flex align-items-end">
                      <ActionButton
                        variant="outline-danger"
                        className="w-100"
                        onClick={() =>
                          setWaActionRows((waActionRows || []).filter((_, i) => i !== idx))
                        }
                      >
                        <BsTrash />
                      </ActionButton>
                    </Col>
                  </Row>
                ))
              )}
            </SubSectionCard>
          </Col>

          <Col xl={12} className="d-flex justify-content-end">
            <ActionButton variant="primary" onClick={saveWA}>
              <BsSave />
              Save WhatsApp Settings
            </ActionButton>
          </Col>
        </Row>
      </SectionCard>

      <SectionCard
        title="Live Support Agents"
        icon={BsHeadset}
        badge={
          <Badge bg="primary" pill>
            {agents.length}
          </Badge>
        }
        className="mt-4"
      >
        <Row className="g-4">
          {agents.length === 0 ? (
            <Col xs={12}>
              <SubSectionCard title="No Agents Added">
                <div className="text-muted">Add your first support agent to begin handling live handovers.</div>
              </SubSectionCard>
            </Col>
          ) : (
            agents.map((a, idx) => (
              <Col xl={6} key={a.id || idx}>
                <SubSectionCard title={`Agent ${idx + 1}`}>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          value={a.name || ''}
                          onChange={(e) => {
                            const next = [...agents];
                            next[idx] = { ...next[idx], name: e.target.value };
                            setAgents(next);
                          }}
                          placeholder="Agent name"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          value={a.email || ''}
                          onChange={(e) => {
                            const next = [...agents];
                            next[idx] = { ...next[idx], email: e.target.value };
                            setAgents(next);
                          }}
                          placeholder="Agent email"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Password</Form.Label>
                        <Form.Control
                          type="password"
                          value={a.password || ''}
                          onChange={(e) => {
                            const next = [...agents];
                            next[idx] = { ...next[idx], password: e.target.value };
                            setAgents(next);
                          }}
                          placeholder="Password"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Max Active Chats</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          value={a.max_active_chats || 10}
                          onChange={(e) => {
                            const next = [...agents];
                            next[idx] = {
                              ...next[idx],
                              max_active_chats: Number(e.target.value || 10),
                            };
                            setAgents(next);
                          }}
                          placeholder="Max active chats"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        label="Online"
                        checked={!!a.is_online}
                        onChange={(e) => {
                          const next = [...agents];
                          next[idx] = { ...next[idx], is_online: e.target.checked };
                          setAgents(next);
                        }}
                      />
                    </Col>

                    <Col md={6}>
                      <Form.Check
                        type="switch"
                        label="Active"
                        checked={!!a.is_active}
                        onChange={(e) => {
                          const next = [...agents];
                          next[idx] = { ...next[idx], is_active: e.target.checked };
                          setAgents(next);
                        }}
                      />
                    </Col>

                    <Col xs={12}>
                      <ActionButton
                        variant="outline-danger"
                        className="w-100"
                        onClick={() => removeAgent(idx)}
                      >
                        <BsTrash />
                        Remove Agent
                      </ActionButton>
                    </Col>
                  </Row>
                </SubSectionCard>
              </Col>
            ))
          )}

          <Col xs={12}>
            <div className="d-flex flex-wrap gap-2 justify-content-end">
              <ActionButton variant="outline-secondary" onClick={addAgent}>
                <BsPlusCircle />
                Add Agent
              </ActionButton>
              <ActionButton variant="primary" onClick={saveAgents}>
                <BsSave />
                Save Agents
              </ActionButton>
            </div>
          </Col>
        </Row>
      </SectionCard>

      <SectionCard
        title="AI Model & Provider Settings"
        icon={BsRobot}
        badge={
          <Badge bg="dark" pill>
            {activeProviderLabel}
          </Badge>
        }
        className="mt-4"
      >
        <Row className="g-4">
          <Col xl={4}>
            <SubSectionCard title="Provider Selection">
              <Row className="g-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>Active Provider</Form.Label>
                    <Form.Select
                      value={model.current_model || 'tinyllama'}
                      onChange={(e) =>
                        setModel({ ...model, current_model: e.target.value })
                      }
                    >
                      <option value="tinyllama">tinyllama</option>
                      <option value="mistral">mistral</option>
                      <option value="openai">openai</option>
                      <option value="gemini">gemini</option>
                      <option value="groq">groq</option>
                    </Form.Select>
                  </Form.Group>
                </Col>

              </Row>
            </SubSectionCard>
          </Col>

          <Col xl={8}>
            <SubSectionCard title={`${activeProviderLabel} Settings`}>
              {model.current_model === 'tinyllama' || model.current_model === 'mistral' ? (
                <div className="text-muted">
                  <div className="fw-semibold mb-2">
                    {activeProviderLabel} does not require external API key fields here.
                  </div>
                  <div className="small">
                    Select this provider and save to make it your active model.
                  </div>
                </div>
              ) : null}

              {model.current_model === 'openai' ? (
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>OpenAI API Key</Form.Label>
                      <Form.Control
                        value={model.openai_key || ''}
                        onChange={(e) =>
                          setModel({ ...model, openai_key: e.target.value })
                        }
                        placeholder="OpenAI key"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>OpenAI Model Name</Form.Label>
                      <Form.Control
                        value={model.openai_model || ''}
                        onChange={(e) =>
                          setModel({ ...model, openai_model: e.target.value })
                        }
                        placeholder="e.g. gpt-4.1-mini"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              ) : null}

              {model.current_model === 'gemini' ? (
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Gemini API Key</Form.Label>
                      <Form.Control
                        value={model.gemini_key || ''}
                        onChange={(e) =>
                          setModel({ ...model, gemini_key: e.target.value })
                        }
                        placeholder="Gemini key"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Gemini Model Name</Form.Label>
                      <Form.Control
                        value={model.gemini_model || ''}
                        onChange={(e) =>
                          setModel({ ...model, gemini_model: e.target.value })
                        }
                        placeholder="e.g. gemini-1.5-pro"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              ) : null}

              {model.current_model === 'groq' ? (
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Groq API Key</Form.Label>
                      <Form.Control
                        value={model.groq_key || ''}
                        onChange={(e) =>
                          setModel({ ...model, groq_key: e.target.value })
                        }
                        placeholder="Groq key"
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Groq Model Name</Form.Label>
                      <Form.Control
                        value={model.groq_model || ''}
                        onChange={(e) =>
                          setModel({ ...model, groq_model: e.target.value })
                        }
                        placeholder="e.g. llama-3.1-8b-instant"
                      />
                    </Form.Group>
                  </Col>
                </Row>
              ) : null}
            </SubSectionCard>
          </Col>

          <Col xl={12}>
            <div className="d-flex justify-content-end">
              <ActionButton variant="primary" onClick={saveModel}>
                <BsSave />
                Save Model Settings
              </ActionButton>
            </div>
          </Col>
        </Row>
      </SectionCard>
    </UserLayout>
  );
}
